import RepostIcon from '@/assets/repost.svg?react'
import { ErrorMessage } from '@hookform/error-message'
import { Button } from '@mantine/core'
import { motion } from 'motion/react'
import type { RefObject } from 'react'
import { Controller, useFormContext, useWatch, type SubmitHandler } from 'react-hook-form'
import AttachmentsPreview from '../Attachment/AttachmentsPreview'
import MixedAttachmentPicker from '../Attachment/pickers/MixedAttachmentPicker'
import type useAttachmentsHelper from '../Attachment/useAttachmentsHelper'
import { toAttachments } from '../Attachment/utils'
import type { LexicalEditorHandle } from '../Editor/LexicalEditor'
import CommentContentEditor, { type CommentContentEditorProps } from './CommentContentEditor'
import type { CommentFormValue, ReplyToComment } from './schema'
import styles from './CommentForm.module.css'

export interface CommentFormFieldsProps extends Pick<
	CommentContentEditorProps,
	'autoFocus' | 'placeholder'
> {
	allowSyncToPersonalUpdate: boolean
	attachmentHandlers: Pick<
		ReturnType<typeof useAttachmentsHelper>,
		| 'handleAttachmentAdd'
		| 'handleAttachmentDelete'
		| 'handleAttachmentRetry'
		| 'handlePasteAttachments'
	>
	contentEditorRef: RefObject<LexicalEditorHandle | null>
	onSubmit: SubmitHandler<CommentFormValue>
	submitLabel: string
	to?: ReplyToComment['to']
}

export default function CommentFormFields({
	allowSyncToPersonalUpdate,
	attachmentHandlers,
	autoFocus,
	contentEditorRef,
	onSubmit,
	placeholder,
	submitLabel,
	to,
}: CommentFormFieldsProps) {
	const methods = useFormContext<CommentFormValue>()
	const isSyncToPersonalUpdate = useWatch({
		control: methods.control,
		name: 'isSyncToPersonalUpdate',
	})
	return (
		<>
			<Controller
				name="content"
				render={({ field }) => (
					<CommentContentEditor
						autoFocus={autoFocus}
						placeholder={placeholder}
						to={to}
						onPaste={attachmentHandlers.handlePasteAttachments}
						onSubmit={methods.handleSubmit(onSubmit)}
						{...field}
						ref={contentEditorRef}
					/>
				)}
			/>
			<footer className={styles.footer}>
				<Controller
					name="attachment"
					render={({ field }) => (
						<MixedAttachmentPicker
							noVideo
							noLink
							isPicturePickerDisabled={field.value?.ids?.length > 0}
							onAdd={attachmentHandlers.handleAttachmentAdd}
							className={styles.attachmentPicker}
						/>
					)}
				/>
				<div className={styles.buttonGroup}>
					{allowSyncToPersonalUpdate && (
						<Controller
							name="isSyncToPersonalUpdate"
							render={({ field }) => (
								<Button
									onClick={() => field.onChange(!field.value)}
									variant={field.value ? 'filled' : 'outline'}
									className={styles.syncButton}
									size="sm"
								>
									<RepostIcon className={styles.syncIcon} color="currentColor" />
								</Button>
							)}
						/>
					)}
					<Button size="sm" type="submit">
						{submitLabel}
						{allowSyncToPersonalUpdate && isSyncToPersonalUpdate && '并同步到动态'}
					</Button>
				</div>
			</footer>
			<Controller
				name="attachment"
				render={({ field }) => (
					<AttachmentsPreview
						data={toAttachments(field.value)}
						onDelete={attachmentHandlers.handleAttachmentDelete}
						onRetry={attachmentHandlers.handleAttachmentRetry}
					/>
				)}
			/>
			<ErrorMessage
				name="attachment"
				errors={methods.formState.errors}
				render={({ message }) => (
					<motion.p
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						exit={{ opacity: 0 }}
						className={styles.errorMessage}
					>
						{message}
					</motion.p>
				)}
			/>
		</>
	)
}
